import {logError} from '../fingerprint.js';
import stableHash from './stableHash.js';
import {noopLap} from './timings.js';

/**
 * Собирает информацию о поддерживаемых аудиоформатах.
 * @returns {IAudioFormatData} Объект с поддерживаемыми форматами аудио.
 */
function collectAudioFormats() {
    const audioElement = document.createElement('audio');

    return {
        MP3: audioElement.canPlayType('audio/mpeg'),
        FLAC: audioElement.canPlayType('audio/flac'),
    };
}

/**
 * Проводит анализ частот аудиосигнала.
 * 1: Количество каналов (1 = моно, 2 = стерео)
 * bufferSize: Длина аудио буфера в сэмплах
 * 44100: Частота дискретизации
 * @param {Window} window
 * @returns {Promise<Float32Array|number[]>} Данные частотного анализа.
 */
async function frequencyAnalyser(window) {
    try {
        const userAgent = navigator.userAgent.toLowerCase();
        const isAndroid = userAgent.indexOf('android') > -1;
        const bufferSize = isAndroid ? 100000 : 44100; // для Android увеличиваем буфер ради стабильности

        const ctx = new (window.OfflineAudioContext || window.webkitOfflineAudioContext)(1, bufferSize, 44100);

        const oscillator = ctx.createOscillator();
        const analyser = ctx.createAnalyser();

        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(440, 0);

        oscillator.connect(analyser);
        analyser.connect(ctx.destination);

        oscillator.start();
        await ctx.startRendering();

        const bins = new Float32Array(analyser.frequencyBinCount);
        analyser.getFloatFrequencyData(bins);
        return bins;
    } catch (e) {
        logError(e);
        return [];
    }
}

/**
 * Собирает данные об аудиоустройствах.
 * С получением списка устройств проблема в большинстве браузеров, нужно разрешение. Firefox частично отдает данные.
 *
 * @param {Window} window
 * @returns {Promise<IAudioHardwareData>} Возвращает объект с данными об аудио устройствах.
 */
async function collectAudioHardwareData(window) {
    if (!isSecureContext) {
        return { audioDevices: null };
    }

    try {
        if (
            typeof window.navigator === 'undefined' ||
            typeof window.navigator.mediaDevices === 'undefined' ||
            typeof window.navigator.mediaDevices.enumerateDevices !== 'function'
        ) {
            return { audioDevices: null };
        }

        const devices = await window.navigator.mediaDevices.enumerateDevices(); // тут проблема с получением девавйсов

        const audioDevices = devices
            .filter(device => device.kind === 'audioinput' || device.kind === 'audiooutput')
            .map(device => ({
                label: device.label || 'Неизвестное устройство',
                kind: device.kind,
                deviceId: device.deviceId,
            }));

        return { audioDevices };
    } catch (error) {
        logError(error);
        return { audioDevices: null };
    }
}


/**
 * Генерация аудио-отпечатка устройства с использованием Web Audio API.
 * Используется ультразвуковой сигнал и поведение браузера при его обработке.
 * @param {Window} window
 * @returns {Promise<IAudioUltrasonicHash>}
 */
async function collectAudioUltrasonicHash(window) {
    let audioCtx = null;
    try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)();
        const offlineCtx = new window.OfflineAudioContext(1, audioCtx.sampleRate * 2, audioCtx.sampleRate);
        const oscillator = offlineCtx.createOscillator();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(18000, 0);
        oscillator.frequency.linearRampToValueAtTime(22000, 1);

        const gainNode = offlineCtx.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(offlineCtx.destination);

        oscillator.start();
        oscillator.stop(1.5);

        const renderedBuffer = await offlineCtx.startRendering();
        const data = renderedBuffer.getChannelData(0);

        let fingerprint = '';
        const sections = 10;

        for (let i = 0; i < sections; i++) {
            const start = Math.floor(i * data.length / sections);
            const end = Math.floor((i + 1) * data.length / sections);
            // Keep the original two passes and addition order for identical hashes.
            let sum = 0;
            for (let sample = start; sample < end; sample++) {
                sum += data[sample];
            }
            const avg = sum / (end - start);
            let squaredDifferences = 0;
            for (let sample = start; sample < end; sample++) {
                squaredDifferences += Math.pow(data[sample] - avg, 2);
            }
            const variance = squaredDifferences / (end - start);

            fingerprint += `${(avg * 10000).toFixed(2)}|${(variance * 1000000).toFixed(2)};`;
        }

        return stableHash(fingerprint);
    } catch (e) {
        logError(e);
        return null;
    } finally {
        try {
            if (audioCtx) {
                audioCtx.close();
            }
        } catch (e1) {
            logError(e1);
        }
    }
}

function calculateHash(samples) {
    let hash = 0;
    for (const element of samples) {
        hash += Math.abs(element);
    }
    return hash;
}

/**
 * Собирает данные о поддержке аудио, включая частотный анализ, аппаратные данные и форматы.
 * С получением списка устройств проблема в большинстве браузеров, нужно разрешение. Firefox частично отдает данные.
 * @param {Window} window
 * @param {function(string)} lap Отметка этапа для замеров (timings.js).
 * @returns {Promise<ICollectAudioData>} Возвращает объект с данными о поддержке аудио.
 */
async function collectAudio(window, lap = noopLap) {
    const audioFormats = collectAudioFormats();
    lap('formats');
    const done = step => value => {
        lap(step);
        return value;
    };
    const hardwarePromise = collectAudioHardwareData(window).then(done('devices'));
    // Аудиоконтексты создаём после синхронного блока fingerprint(): не задерживаем запуск остальных коллекторов.
    await Promise.resolve();
    const [hardwareData, frequencyData, ultrasonicHash] = await Promise.all([
        hardwarePromise,
        frequencyAnalyser(window).then(done('frequency')),
        collectAudioUltrasonicHash(window).then(done('ultrasonic')),
    ]);
    const audioUltrasonic = ultrasonicHash ?? 0;
    /** @type IFrequencyDataHash */
    const frequencyDataHash = calculateHash(frequencyData)?.toString() ?? null;

    return {
        ...audioFormats,
        ...hardwareData,
        audioUltrasonic,
        frequencyDataHash,
    };
}

export default collectAudio;
